import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { getOutlets, extractOutletId } from "../api/outlets";
import { getTenantConfig } from "../services/tenants";
import { generateDeviceFingerprint } from "../utils/utils";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [outletId, setOutletId] = useState(null);
  const [hasOverhaulEnabled, setHasOverhaulEnabled] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const pageUrl = window.location.href;
        const isLocalhost = pageUrl.includes("localhost") || pageUrl.includes("127.0.0.1");
        const isProdEnv = process.env.NODE_ENV === "production" && 
                         !pageUrl.includes("qa") && 
                         !pageUrl.includes("pr");

        const baseUrl = isLocalhost 
          ? "https://amer-qa.collinsondesk.com/qa-v3"
          : isProdEnv 
            ? "https://amer.collinsondesk.com/v3"
            : "https://amer-qa.collinsondesk.com/qa-v3";

        const deviceFingerprint = await generateDeviceFingerprint();

        const response = await axios({
          method: 'POST',
          url: `${baseUrl}/auth/anonymous`,
          headers: { 'Content-Type': 'application/json' },
          data: {
            fingerprint: deviceFingerprint,
          },
        });

        const headerKeys = Object.keys(response.headers);
        const tokenHeader = headerKeys.find(key => 
          key.toLowerCase() === 'access-token'
        );
        const accessToken = tokenHeader ? response.headers[tokenHeader] : null;
        
        // Check for session ID in response headers
        const sessionHeader = headerKeys.find(key => 
          key.toLowerCase() === 'session' || key.toLowerCase() === 'session-id'
        );
        const sessionIdFromAuth = sessionHeader ? response.headers[sessionHeader] : null;
        
        // Also check for session ID in Set-Cookie header (cookies)
        const setCookieHeader = response.headers['set-cookie'] || response.headers['Set-Cookie'];
        let sessionIdFromCookie = null;
        if (setCookieHeader) {
          const cookies = Array.isArray(setCookieHeader) ? setCookieHeader : [setCookieHeader];
          for (const cookie of cookies) {
            const sessionMatch = cookie.match(/session[Ii][Dd]?=([^;]+)/);
            if (sessionMatch) {
              sessionIdFromCookie = sessionMatch[1];
              break;
            }
          }
        }
        
        const finalSessionId = sessionIdFromAuth || sessionIdFromCookie;
        if (finalSessionId) {
          localStorage.setItem('sessionId', finalSessionId);
        }

        if (accessToken) {
          setToken(accessToken);
          localStorage.setItem('anonymousToken', accessToken);
          
          // First, get outletId to fetch tenant config
          try {
            const pageUrl = window.location.href;
            const urlPath = new URL(pageUrl).pathname;
            const loungeCode = !pageUrl.includes("localhost")
              ? urlPath.substring(urlPath.lastIndexOf("/") + 1)
              : process.env.REACT_APP_TEST_LOUNGE_CODE;

            // Call outlets to get outletId
            const outletsResponse = await getOutlets(baseUrl, {
              limit: 25,
              page: 1,
              search: loungeCode
            });
            
            const extractedOutletId = extractOutletId(outletsResponse);
            if (extractedOutletId) {
              setOutletId(extractedOutletId);
              localStorage.setItem('outletId', extractedOutletId);
              
              // Fetch tenant config to get featureFlags
              try {
                const tenantConfig = await getTenantConfig(extractedOutletId, baseUrl);
                const featureFlagsConfig = tenantConfig?.find(
                  (config) => config.configType === 'featureFlags'
                );
                
                const hasOverhaul = featureFlagsConfig?.value?.hasOverhaulEnabled ?? false;
                setHasOverhaulEnabled(hasOverhaul);
                localStorage.setItem('hasOverhaulEnabled', hasOverhaul.toString());
              } catch (configError) {
                // Don't throw - we can continue without feature flags
                setHasOverhaulEnabled(false);
                localStorage.setItem('hasOverhaulEnabled', 'false');
              }
            }
          } catch (outletsError) {
            // Don't throw - we can continue without outletId
          }
        }
      } catch (error) {
        // Error fetching anonymous token - silently fail
      } finally {
        setIsLoading(false);
      }
    };

    fetchToken();
  }, []);

  const hasToken = !!token || !!localStorage.getItem('anonymousToken');
  const getToken = () => token || localStorage.getItem('anonymousToken');
  const getOutletId = () => outletId || localStorage.getItem('outletId');
  const getHasOverhaulEnabled = () => {
    if (hasOverhaulEnabled !== null) {
      return hasOverhaulEnabled;
    }
    const stored = localStorage.getItem('hasOverhaulEnabled');
    return stored === 'true';
  };

  return (
    <AuthContext.Provider value={{
      token: getToken(),
      hasToken,
      isLoading,
      outletId: getOutletId(),
      hasOverhaulEnabled: getHasOverhaulEnabled(),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
